// @flow
import { createSelector } from 'reselect';

// selectors
import {
  getStartTime,
  getAttendanceStatus,
  getTransportationOption,
  getLifecycleState
} from 'store/reducers/appointment/selectors';
import {
  getSelfCheckInStatus,
  getStatusTrackerURL,
  getSelfCheckInFlow
} from 'store/reducers/selfCheckIn/selectors';
import {
  getSelfCheckInEnabled,
  getSelfCheckInExtendedEnabled
} from 'store/reducers/userInteractions/selectors';

// Constants
import {
  VALID_STATUSES_ARRAY,
  IN_PROGRESS_STATUSES_ARRAY,
  ERROR_TYPE,
  LIFECYCLE_STATUS
} from 'constants/selfCheckIn';
import { TRANSPORTATION } from 'constants/transportation';

// Utils
import { isEmpty } from 'utils/string';

const {
  TOO_EARLY,
  ALREADY_IN_PROGRESS,
  NOT_AVAILABLE,
  CANCELLED_OR_PAST
} = ERROR_TYPE;
const { VALET } = TRANSPORTATION;
const { CANCELLED } = LIFECYCLE_STATUS;

const isStatusValid = createSelector(
  getAttendanceStatus,
  attendanceStatus => VALID_STATUSES_ARRAY.indexOf(attendanceStatus) > -1
);

export const isAlreadyInProgressError = createSelector(
  getAttendanceStatus,
  attendanceStatus => {
    return IN_PROGRESS_STATUSES_ARRAY.indexOf(attendanceStatus) > -1;
  }
);

export const selfCheckInError = createSelector(
  getSelfCheckInEnabled,
  getSelfCheckInExtendedEnabled,
  getTransportationOption,
  getSelfCheckInFlow,
  (
    isSelfCheckInEnabled,
    isSelfCheckInExtendedEnabled,
    transportationOption,
    selfCheckInFlow
  ) => {
    return (
      !selfCheckInFlow ||
      !isSelfCheckInEnabled ||
      transportationOption === VALET
    );
  }
);

export const isTooEarly = createSelector(getStartTime, startTime => {
  if (startTime) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const apptTime = new Date(startTime);
    apptTime.setHours(0, 0, 0, 0);
    return apptTime > today;
  }
  return null;
});

export const isHasAlreadyBeenSCI = createSelector(
  isAlreadyInProgressError,
  getSelfCheckInStatus,
  (isInProgress, selfCheckInStatus) => {
    return !isInProgress && selfCheckInStatus;
  }
);

export const isApptCancelled = createSelector(
  getLifecycleState,
  lifecycle => lifecycle === CANCELLED
);

export const isPastAppointment = createSelector(getStartTime, startTime => {
  if (startTime) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const apptTime = new Date(startTime);
    apptTime.setHours(0, 0, 0, 0);
    return apptTime < today;
  }
  return null;
});

export const getShowStatusButton = createSelector(
  getStatusTrackerURL,
  statusTrackerURL => {
    return !isEmpty(statusTrackerURL);
  }
);

export const getErrorType = createSelector(
  isTooEarly,
  isAlreadyInProgressError,
  isApptCancelled,
  isPastAppointment,
  selfCheckInError,
  isStatusValid,
  isHasAlreadyBeenSCI,
  (
    tooEarlyError,
    alreadyInProgressError,
    cancelledApptError,
    pastApptError,
    selfCheckInError,
    isStatusValid,
    isSelfCheckedIn
  ) => {
    let errorType;
    if (alreadyInProgressError) {
      errorType = ALREADY_IN_PROGRESS;
    } else if (cancelledApptError) {
      errorType = CANCELLED_OR_PAST;
    } else {
      if (isSelfCheckedIn) {
        // Already self checked in, redirect to confirmation page
        errorType = null;
      } else if (!isStatusValid) {
        errorType = NOT_AVAILABLE;
      } else if (tooEarlyError) {
        errorType = TOO_EARLY;
      } else if (pastApptError) {
        errorType = CANCELLED_OR_PAST;
      } else if (selfCheckInError) {
        errorType = NOT_AVAILABLE;
      } else {
        // Allow regular check in flow
        errorType = null;
      }
    }
    return errorType;
  }
);



// WEBPACK FOOTER //
// ./src/store/reducers/appointment/selfCheckIn/selectors.js